import {
  CustomAction,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FormDialogMode,
  RowCustomAction,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Block, Delete, Flag } from '@material-ui/icons'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCriticidad } from '../../../api/oData/enums/enumCriticidad'
import { EnumDominioTarea } from '../../../api/oData/enums/enumDominioTarea'
import { EnumEstadoTarea } from '../../../api/oData/enums/enumEstadoTarea'
import { enumToSelectOptionArray } from '../../../helpers'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import {
  FormDialogDefinitionProps,
  FormDialogHookProps,
  HttpErrorProps,
  useFormDialog
} from '../../../hooks'
import { listObjDescriptionsItemMapper } from '../../../pages/invoicesPage/invoicePage.mapper'
import { ObjectionTableModel } from '../../../pages/invoicesPage/invoicesPage.Model'
import { fetchObjectionsDescriptions } from '../../../pages/invoicesPage/invoicesPage.service'
import { getCustomerDomain } from '../../../redux'
import { DataTableErrorHandler } from '../../errors/scopes/dataTable/dataTableError'

interface ObjectionsDataTableProps {
  title?: string | React.ReactNode
  objDescriptions: ObjectionTableModel[]
  objectionsData?: ObjectionTableModel[]
  errorProps?: HttpErrorProps & { title?: string }
  allowInsert?: boolean
  showDescription?: boolean
  showCriticality?: boolean
  showState?: boolean
  showIdObjMaestra?: boolean
  hasEditPermission?: boolean
  tableUserPermissions?: UserPermission[]
  taskDomain?: EnumDominioTarea
  onRowDelete?: (row: ObjectionTableModel) => Promise<void>
  onRowAdd?: (row: ObjectionTableModel) => Promise<void>
  onTerminateObjection?: (row: ObjectionTableModel) => Promise<void>
  onBulkTerminate?: (selectedRows: ObjectionTableModel[]) => Promise<void>
  onUpdateCriticality?: (rows: ObjectionTableModel[], criticality: EnumCriticidad) => Promise<void>
}

export const ObjectionsDataTable: React.FC<ObjectionsDataTableProps> = ({
  title,
  objDescriptions,
  objectionsData,
  showDescription = false,
  errorProps,
  allowInsert = false,
  hasEditPermission = false,
  showCriticality = true,
  showState = false,
  showIdObjMaestra = false,
  tableUserPermissions,
  taskDomain,
  onRowDelete,
  onRowAdd,
  onTerminateObjection,
  onBulkTerminate,
  onUpdateCriticality
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'objectionsDataTable'])
  const { openForm, updateFormData, updateFormDefinition, closeForm, formProps, CustomFormDialog } =
    useFormDialog()
  const {
    openForm: openCriticalityForm,
    closeForm: closeCriticalityForm,
    formProps: criticalityFormProps,
    CustomFormDialog: CriticalityFormDialog
  } = useFormDialog()
  const { error } = useNotify()

  const [formMode, setFormMode] = useState<FormDialogMode | null>(null)
  const [isLoadingDescriptions, setIsLoadingDescriptions] = useState<boolean>(false)
  const [descriptionsOptions, setDescriptionsOptions] = useState<SelectOption[]>([])

  // #region Handlers Starts
  const handleInsertForm = async () => {
    setFormMode(FormDialogMode.ADD)

    descriptionsOptions.length === 0 && getDescriptions() // Fetch descriptions options

    openForm({
      definition: objectionFormDefinition,
      title: t('objectionsDataTable:forms.add.title'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: formData => {
        handleFormClose(formData, onRowAdd)
      }
    })
    return true
  }

  const handleFormClose = async (
    formData: ObjectionTableModel,
    callback?: (formData: ObjectionTableModel) => Promise<void>
  ) => {
    let formIsIncomplete: boolean = false
    if (formData) {
      if (
        !missingFieldsFormWithRequiredText(objectionFormDefinitionRef.current.sections!, formData)
      ) {
        callback && (await callback(formData))
      } else {
        error(t('objectionsDataTable:forms.notifications.incompleteForm'))
        formIsIncomplete = true
      }
    }
    if (!formIsIncomplete) {
      closeForm()
      setFormMode(null)
    }
  }

  const handleOpenCriticalityForm = (rows: ObjectionTableModel[]) => {
    pendingCriticalityRowsRef.current = rows
    openCriticalityForm({
      definition: criticalityFormDefinitionRef.current,
      title: t('objectionsDataTable:forms.criticality.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: handleCriticalityFormClose
    } as FormDialogHookProps)
  }

  const handleCriticalityFormClose = async (formData: any) => {
    if (formData) {
      if (
        !missingFieldsFormWithRequiredText(criticalityFormDefinitionRef.current.sections!, formData)
      ) {
        onUpdateCriticality &&
          (await onUpdateCriticality(
            pendingCriticalityRowsRef.current,
            formData.criticalityId as EnumCriticidad
          ))
        closeCriticalityForm()
      } else {
        error(t('objectionsDataTable:forms.notifications.incompleteForm'))
      }
    } else {
      closeCriticalityForm()
    }
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  const objectionFormDefinition = React.useMemo<FormDialogDefinitionProps>(
    (): FormDialogDefinitionProps => ({
      id: 'ADD_OBJECTION',
      sections: [
        {
          inputs: [
            {
              name: 'idObjMaestra',
              label: t('objectionsDataTable:forms.add.inputs.description'),
              type: DataType.SELECT,
              selectOptions: descriptionsOptions,
              isLoading: isLoadingDescriptions,
              required: true
            },
            {
              name: 'comments',
              label: t('objectionsDataTable:forms.add.inputs.comments'),
              type: DataType.TEXT,
              required: false
            },
            {
              name: 'state',
              label: t('objectionsDataTable:forms.add.inputs.state'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumEstadoTarea, 'enums:EnumEstadoTarea'),
              defaultValue: EnumEstadoTarea.Nueva,
              readOnly: true
            }
          ]
        }
      ]
    }),
    [descriptionsOptions, isLoadingDescriptions]
  )

  const criticalityFormDefinition = React.useMemo<FormDialogDefinitionProps>(
    (): FormDialogDefinitionProps => ({
      id: 'UPDATE_CRITICALITY',
      sections: [
        {
          inputs: [
            {
              name: 'criticalityId',
              label: t('objectionsDataTable:forms.criticality.inputs.criticality'),
              type: DataType.SELECT,
              selectOptions: enumToSelectOptionArray(EnumCriticidad, 'enums:EnumCriticidad'),
              required: true
            }
          ]
        }
      ]
    }),
    [t]
  )
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const objColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'idObjMaestra',
      label: t('objectionsDataTable:columns.description'),
      type: DataType.SELECT,
      selectOptions: listObjDescriptionsItemMapper(objDescriptions),
      display: showIdObjMaestra,
      columnWidth: 200
    },
    {
      name: 'description',
      label: t('objectionsDataTable:columns.description'),
      type: DataType.TEXT,
      display: showDescription,
      columnWidth: 200
    },
    {
      name: 'criticalityId',
      label: t('objectionsDataTable:columns.criticality'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumCriticidad, 'enums:EnumCriticidad'),
      display: showCriticality,
      columnWidth: 60
    },
    {
      name: 'comments',
      label: t('objectionsDataTable:columns.comments'),
      type: DataType.TEXT,
      columnWidth: 200
    },
    {
      name: 'state',
      label: t('objectionsDataTable:columns.state'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumEstadoTarea, 'enums:EnumEstadoTarea'),
      defaultValue: EnumEstadoTarea.Nueva,
      readOnly: true,
      display: showState,
      columnWidth: 60
    }
  ]

  const getCustomRowActions = () => {
    let rowCustomActions: RowCustomAction[] = []
    if (onRowDelete && hasEditPermission) {
      rowCustomActions = [
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('objectionsDataTable:actions.delete'),
          onClick: onRowDelete
        }
      ]
    }

    if (onTerminateObjection && hasEditPermission) {
      rowCustomActions.push({
        icon: <Block fontSize="small" />,
        tooltip: t('objectionsDataTable:actions.terminate'),
        onClick: onTerminateObjection,
        visibleIf: 'canTerminate'
      })
    }

    if (onUpdateCriticality && hasEditPermission) {
      rowCustomActions.push({
        icon: <Flag fontSize="small" />,
        tooltip: t('objectionsDataTable:actions.updateCriticality'),
        onClick: row => handleOpenCriticalityForm([row])
      })
    }

    return rowCustomActions
  }

  const getSelectToolbarActions = (): CustomAction[] => {
    const actions: CustomAction[] = []
    if (onBulkTerminate && hasEditPermission) {
      actions.push({
        icon: <Block fontSize="small" />,
        tooltip: t('objectionsDataTable:actions.terminate'),
        onClick: selectedRows => onBulkTerminate(selectedRows ?? [])
      })
    }
    if (onUpdateCriticality && hasEditPermission) {
      actions.push({
        icon: <Flag fontSize="small" />,
        tooltip: t('objectionsDataTable:actions.updateCriticality'),
        onClick: selectedRows => handleOpenCriticalityForm(selectedRows ?? [])
      })
    }
    return actions
  }

  const objOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    elevation: 0,
    download: true,
    canEdit: false,
    canDelete: false,
    canInsert: allowInsert && hasEditPermission,
    customInsertEditModal: true,
    selectableRows: !!(onBulkTerminate || onUpdateCriticality),
    analytics: {
      customer: getCustomerDomain(),
      view: t('objectionsDataTable:analytics')
    },
    rowCustomActions: getCustomRowActions(),
    selectToolbarCustomActions: getSelectToolbarActions(),
    onRowInsert: handleInsertForm
  }
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const objectionFormDefinitionRef = useRef(objectionFormDefinition)
  useEffect(() => {
    objectionFormDefinitionRef.current = objectionFormDefinition
  }, [objectionFormDefinition])

  const criticalityFormDefinitionRef = useRef(criticalityFormDefinition)
  useEffect(() => {
    criticalityFormDefinitionRef.current = criticalityFormDefinition
  }, [criticalityFormDefinition])

  const pendingCriticalityRowsRef = useRef<ObjectionTableModel[]>([])

  useEffect(() => {
    updateFormDefinition(objectionFormDefinition)
  }, [objectionFormDefinition])

  const getDescriptions = useCallback(async () => {
    setIsLoadingDescriptions(true)
    await fetchObjectionsDescriptions(taskDomain)
      .then(response => {
        if (response.success && response.data) {
          setDescriptionsOptions(listObjDescriptionsItemMapper(response.data))
        } else {
          error(t('objectionsDataTable:fetchResponse.fetchObjectionsDescriptions.error'))
        }
      })
      .finally(() => {
        setIsLoadingDescriptions(false)
      })
  }, [taskDomain])
  // #endregion Hooks Ends

  return (
    <>
      <DataTable
        title={title ? title : null}
        data={objectionsData}
        columns={objColumns}
        options={objOptions}
        info={errorProps && DataTableErrorHandler(errorProps)}
        userPermissions={tableUserPermissions}
      />
      <CustomFormDialog {...formProps} />
      <CriticalityFormDialog {...criticalityFormProps} maxWidth="sm" />
    </>
  )
}
